/**
 * Hub API Service
 *
 * App-owned hub structure. WordPress is used only for resource post content.
 */

import {
  Hub,
  HubResource,
  HubSection,
  HubWithContent,
  CreateHubInput,
  UpdateHubInput,
  AddResourceToHubInput,
  UpdateHubResourceInput,
  CreateSectionInput,
  UpdateSectionInput,
} from '../types/hub';
import { wordpressAPI } from './wordpressApi';
import { clerkAuthHeaders } from './clerkApiAuth';

const API_BASE_URL = '/api';

const API_NOT_RUNNING_HINT =
  'The website API is not responding on the Vite /api proxy port. Stop extra `npm run dev` processes and start the API with `npm run dev` or `npm run dev:server`.';

function stringifyApiError(error: unknown): string | undefined {
  if (typeof error === 'string' && error.trim()) return error;
  if (error && typeof error === 'object') {
    const record = error as { message?: unknown };
    if (typeof record.message === 'string' && record.message.trim()) return record.message;
    try {
      return JSON.stringify(error);
    } catch {
      return undefined;
    }
  }
  return undefined;
}

async function getApiErrorMessage(response: Response, fallback: string): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('text/html')) {
    return `${fallback}. ${API_NOT_RUNNING_HINT}`;
  }

  if (response.status === 500 && !contentType.includes('application/json')) {
    return `${fallback}. The backend API may not be running — try \`npm run dev\` (starts frontend and backend together).`;
  }

  try {
    const body = await response.json();
    const fromError = stringifyApiError(body?.error);
    if (fromError) {
      return `${fallback}: ${fromError}`;
    }
  } catch {
    // Response body wasn't JSON
  }

  return `${fallback}: ${response.statusText || response.status}`;
}

function wrapFetchError(error: unknown, fallback: string): Error {
  if (error instanceof Error && error.name === 'AbortError') {
    return new Error(`${fallback}. Request timed out. ${API_NOT_RUNNING_HINT}`);
  }
  if (error instanceof SyntaxError) {
    return new Error(`${fallback}. ${API_NOT_RUNNING_HINT}`);
  }
  if (error instanceof Error) return error;
  return new Error(fallback);
}

class HubService {
  async fetchHubs(options?: { includeDrafts?: boolean }): Promise<Hub[]> {
    try {
      const params = options?.includeDrafts ? '?includeDrafts=true' : '';
      const response = await fetch(`${API_BASE_URL}/hubs${params}`, {
        headers: options?.includeDrafts ? await clerkAuthHeaders({ json: false }) : undefined,
        signal: AbortSignal.timeout(8000),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to fetch hubs'));
      }

      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('application/json')) {
        throw new Error(`Failed to fetch hubs. ${API_NOT_RUNNING_HINT}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching hubs:', error);
      throw wrapFetchError(error, 'Failed to fetch hubs');
    }
  }

  async fetchHub(id: string): Promise<Hub> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${id}`, {
        headers: await clerkAuthHeaders({ json: false, optional: true }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to fetch hub'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching hub:', error);
      throw error;
    }
  }

  async fetchHubBySlug(slug: string): Promise<Hub | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/slug/${slug}`);

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to fetch hub'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching hub:', error);
      throw error;
    }
  }

  /**
   * Fetch hub; optionally attach legacy WP landing content when no CONTENT sections exist.
   */
  async fetchHubWithContent(slug: string): Promise<HubWithContent | null> {
    try {
      const hub = await this.fetchHubBySlug(slug);
      if (!hub) return null;

      const hasContentSections = hub.sections?.some((s) => s.type === 'CONTENT');
      if (hasContentSections || !hub.wpPostId) {
        return hub;
      }

      try {
        const wpContent = await wordpressAPI.fetchToolkit(hub.wpPostId);

        let featuredMediaUrl: string | undefined;
        if (wpContent.featured_media) {
          try {
            const media = await wordpressAPI.fetchMedia(wpContent.featured_media);
            featuredMediaUrl = media.source_url;
          } catch (e) {
            console.warn('Failed to fetch featured media:', e);
          }
        }

        return {
          ...hub,
          wpContent: {
            content: wpContent.content.rendered,
            excerpt: wpContent.excerpt.rendered,
            featuredMedia: wpContent.featured_media,
            featuredMediaUrl,
          },
        };
      } catch (error) {
        console.warn('Failed to fetch WordPress content for hub:', error);
        return hub;
      }
    } catch (error) {
      console.error('Error fetching hub with content:', error);
      throw error;
    }
  }

  async createHub(input: CreateHubInput): Promise<Hub> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs`, {
        method: 'POST',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to create hub'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating hub:', error);
      throw error;
    }
  }

  async updateHub(hubId: string, updates: UpdateHubInput): Promise<Hub> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}`, {
        method: 'PUT',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update hub'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating hub:', error);
      throw error;
    }
  }

  async deleteHub(hubId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}`, {
        method: 'DELETE',
        headers: await clerkAuthHeaders({ json: false }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to delete hub'));
      }
    } catch (error) {
      console.error('Error deleting hub:', error);
      throw error;
    }
  }

  async addResourceToHub(hubId: string, input: AddResourceToHubInput): Promise<HubResource> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}/resources`, {
        method: 'POST',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to add resource to hub'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding resource to hub:', error);
      throw error;
    }
  }

  async updateHubResource(
    hubId: string,
    resourceId: string,
    updates: UpdateHubResourceInput
  ): Promise<HubResource> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}/resources/${resourceId}`, {
        method: 'PUT',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update hub resource'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating hub resource:', error);
      throw error;
    }
  }

  async removeResourceFromHub(hubId: string, resourceId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}/resources/${resourceId}`, {
        method: 'DELETE',
        headers: await clerkAuthHeaders({ json: false }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to remove resource from hub'));
      }
    } catch (error) {
      console.error('Error removing resource from hub:', error);
      throw error;
    }
  }

  async createSection(hubId: string, input: CreateSectionInput): Promise<HubSection> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}/sections`, {
        method: 'POST',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to create section'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating section:', error);
      throw error;
    }
  }

  async updateSection(
    hubId: string,
    sectionId: string,
    updates: UpdateSectionInput
  ): Promise<HubSection> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}/sections/${sectionId}`, {
        method: 'PUT',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to update section'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating section:', error);
      throw error;
    }
  }

  async reorderSections(hubId: string, sectionIds: string[]): Promise<HubSection[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}/sections/reorder`, {
        method: 'PUT',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify({ sectionIds }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to reorder sections'));
      }

      return await response.json();
    } catch (error) {
      console.error('Error reordering sections:', error);
      throw error;
    }
  }

  async deleteSection(hubId: string, sectionId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/hubs/${hubId}/sections/${sectionId}`, {
        method: 'DELETE',
        headers: await clerkAuthHeaders({ json: false }),
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Failed to delete section'));
      }
    } catch (error) {
      console.error('Error deleting section:', error);
      throw error;
    }
  }
}

export const hubAPI = new HubService();
